<template>
  <div class="lobby">
    <div class="lobby-header">
      <h1>Game Lobby</h1>
      <div class="match-info">
        <span class="match-id">Match ID: {{ matchId }}</span>
        <span class="map-name">Map: {{ map }}</span>
        <span class="game-mode">Mode: {{ gameMode }}</span>
      </div>
    </div>
    
    <div class="teams-container">
      <div class="team red-team">
        <h2>Red Team</h2>
        <div class="team-players">
          <div 
            v-for="player in redTeamPlayers" 
            :key="player.id"
            class="player-card"
            :class="{ 'current-player': player.id === currentPlayerId }"
          >
            <div class="player-icon">
              {{ getPlayerInitial(player.username) }}
            </div>
            <div class="player-info">
              <div class="player-name">{{ player.username }}</div>
              <div class="player-character">{{ player.character }}</div>
              <div class="player-status" :class="player.status.toLowerCase()">
                {{ player.status }}
              </div>
            </div>
          </div>
          <div 
            v-for="n in (maxTeamSize - redTeamPlayers.length)" 
            :key="`empty-red-${n}`"
            class="player-card empty"
          >
            <div class="player-icon empty">?</div>
            <div class="player-info">
              <div class="player-name">Waiting for player...</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="team blue-team">
        <h2>Blue Team</h2>
        <div class="team-players">
          <div 
            v-for="player in blueTeamPlayers" 
            :key="player.id"
            class="player-card"
            :class="{ 'current-player': player.id === currentPlayerId }"
          >
            <div class="player-icon">
              {{ getPlayerInitial(player.username) }}
            </div>
            <div class="player-info">
              <div class="player-name">{{ player.username }}</div>
              <div class="player-character">{{ player.character }}</div>
              <div class="player-status" :class="player.status.toLowerCase()">
                {{ player.status }}
              </div>
            </div>
          </div>
          <div 
            v-for="n in (maxTeamSize - blueTeamPlayers.length)" 
            :key="`empty-blue-${n}`"
            class="player-card empty"
          >
            <div class="player-icon empty">?</div>
            <div class="player-info">
              <div class="player-name">Waiting for player...</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="match-status">
      <div class="countdown" v-if="isCountingDown">
        Match starting in {{ countdownSeconds }} seconds...
      </div>
      <div class="player-count" v-else>
        Waiting for players... ({{ redTeamPlayers.length + blueTeamPlayers.length }}/{{ maxTeamSize * 2 }})
      </div>
    </div>
    
    <div class="chat-section">
      <div class="chat-messages" ref="chatMessages">
        <div 
          v-for="(message, index) in chatMessages" 
          :key="index"
          class="chat-message"
          :class="{ 'system-message': message.type === 'system' }"
        >
          <span v-if="message.type !== 'system'" class="message-sender" :class="message.team.toLowerCase()">
            {{ message.sender }}:
          </span>
          <span class="message-text">{{ message.text }}</span>
        </div>
      </div>
      <div class="chat-input">
        <input 
          type="text" 
          v-model="chatInput" 
          placeholder="Type a message..." 
          @keyup.enter="sendChatMessage"
        />
        <button @click="sendChatMessage">Send</button>
      </div>
    </div>
    
    <div class="lobby-actions">
      <button class="leave-button" @click="leaveLobby">Leave Lobby</button>
      <button 
        class="ready-button" 
        :class="{ 'ready': isReady }"
        @click="toggleReady"
      >
        {{ isReady ? 'Not Ready' : 'Ready' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Lobby',
  data() {
    return {
      matchId: 'M1249', // This would come from the server
      map: 'Warehouse',
      gameMode: 'Team Deathmatch',
      maxTeamSize: 4,
      currentPlayerId: 'player1', // This would come from authentication
      isReady: false,
      isCountingDown: false,
      countdownSeconds: 10,
      chatInput: '',
      chatMessages: [
        // Sample chat messages
        { type: 'system', text: 'Welcome to the lobby! Get ready for the match.' },
        { sender: 'Player2', team: 'Red', text: 'Hello everyone!' },
        { sender: 'Player3', team: 'Blue', text: 'Good luck!' }
      ],
      // Sample player data - would come from server
      players: [
        { id: 'player1', username: 'Player1', team: 'Red', character: 'Soldier', status: 'NotReady' },
        { id: 'player2', username: 'Player2', team: 'Red', character: 'Scout', status: 'Ready' },
        { id: 'player3', username: 'Player3', team: 'Blue', character: 'Soldier', status: 'Ready' },
        { id: 'player4', username: 'Player4', team: 'Blue', character: 'Scout', status: 'NotReady' }
      ]
    };
  },
  computed: {
    redTeamPlayers() {
      return this.players.filter(player => player.team === 'Red');
    },
    blueTeamPlayers() {
      return this.players.filter(player => player.team === 'Blue');
    }
  },
  methods: {
    getPlayerInitial(username) {
      return username ? username.charAt(0).toUpperCase() : '?';
    },
    toggleReady() {
      this.isReady = !this.isReady;
      // This would update the player's ready status on the server
      const currentPlayer = this.players.find(p => p.id === this.currentPlayerId);
      if (currentPlayer) {
        currentPlayer.status = this.isReady ? 'Ready' : 'NotReady';
      }
      
      // For demo purposes: if all players are ready, start countdown
      if (this.isReady && this.players.every(p => p.status === 'Ready')) {
        this.startCountdown();
      }
    },
    startCountdown() {
      this.isCountingDown = true;
      const interval = setInterval(() => {
        this.countdownSeconds--;
        if (this.countdownSeconds <= 0) {
          clearInterval(interval);
          this.navigateToGame();
        }
      }, 1000);
    },
    navigateToGame() {
      // This would navigate to the game view
      console.log('Navigating to game view...');
      // this.$router.push('/game'); // Will be implemented with Vue Router
    },
    leaveLobby() {
      // This would return to the main menu
      console.log('Leaving lobby...');
      // this.$router.push('/'); // Will be implemented with Vue Router
    },
    sendChatMessage() {
      if (!this.chatInput.trim()) return;
      
      // Find the current player to get team information
      const currentPlayer = this.players.find(p => p.id === this.currentPlayerId);
      if (currentPlayer) {
        this.chatMessages.push({
          sender: currentPlayer.username,
          team: currentPlayer.team,
          text: this.chatInput.trim()
        });
        
        this.chatInput = '';
        
        // Scroll to bottom of chat
        this.$nextTick(() => {
          const chatContainer = this.$refs.chatMessages;
          chatContainer.scrollTop = chatContainer.scrollHeight;
        });
      }
    }
  }
};
</script>

<style scoped>
.lobby {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px;
  font-family: 'Arial', sans-serif;
}

.lobby-header {
  text-align: center;
  margin-bottom: 30px;
}

h1 {
  margin-bottom: 10px;
  color: #333;
}

.match-info {
  display: flex;
  justify-content: center;
  gap: 20px;
  color: #666;
}

.teams-container {
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
}

.team {
  flex: 1;
  padding: 20px;
  border-radius: 10px;
}

.red-team {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.blue-team {
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
}

.team h2 {
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.team-players {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.player-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.player-card.empty {
  opacity: 0.5;
  background-color: rgba(255, 255, 255, 0.5);
}

.player-card.current-player {
  border: 2px solid #4caf50;
}

.player-icon {
  width: 40px;
  height: 40px;
  background-color: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: bold;
  font-size: 18px;
}

.player-icon.empty {
  background-color: #9e9e9e;
}

.player-info {
  flex: 1;
}

.player-name {
  font-weight: bold;
  margin-bottom: 3px;
}

.player-character {
  font-size: 12px;
  color: #666;
  margin-bottom: 3px;
}

.player-status {
  font-size: 12px;
  font-weight: bold;
}

.player-status.ready {
  color: #4caf50;
}

.player-status.notready {
  color: #f44336;
}

.match-status {
  text-align: center;
  margin-bottom: 30px;
  font-size: 18px;
}

.countdown {
  color: #f44336;
  font-weight: bold;
}

.chat-section {
  margin-bottom: 30px;
  border: 1px solid #ddd;
  border-radius: 10px;
  overflow: hidden;
}

.chat-messages {
  height: 200px;
  overflow-y: auto;
  padding: 15px;
  background-color: #f9f9f9;
}

.chat-message {
  margin-bottom: 10px;
  word-break: break-word;
}

.system-message {
  color: #757575;
  font-style: italic;
}

.message-sender {
  font-weight: bold;
  margin-right: 5px;
}

.message-sender.red {
  color: #f44336;
}

.message-sender.blue {
  color: #2196f3;
}

.chat-input {
  display: flex;
  border-top: 1px solid #ddd;
}

.chat-input input {
  flex: 1;
  padding: 12px 15px;
  border: none;
  outline: none;
  font-size: 14px;
}

.chat-input button {
  padding: 12px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.lobby-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.leave-button {
  padding: 12px 25px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
}

.ready-button {
  padding: 12px 25px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.ready-button.ready {
  background-color: #4caf50;
}

button:hover {
  opacity: 0.9;
}
</style> 